import { observable, action } from 'mobx';
import { DisposableBase } from '../Types';
import React from 'react';
import type { SettingsStore } from '../Setup/Stores/SettingsStore';
import { debugLog } from './DebugLogStore';

const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
    width: { min: 640, ideal: 1280, max: 1920 },
    height: { min: 480, ideal: 720, max: 1080 },
};

// After a background/resume the camera is often not free yet (getUserMedia throws
// NotReadableError "Could not start video source"), so re-acquisition retries with a
// growing backoff instead of giving up on the first failure.
const REACQUIRE_RETRY_DELAYS_MS = [0, 400, 800, 1500, 2500];

// Event-independent safety net: a timer that resumes ticking once the page is active
// again (background timers are paused/throttled by the OS) and re-acquires the camera if
// the stream is frozen — covers WebViews that don't fire visibilitychange/focus on resume.
const STREAM_WATCHDOG_INTERVAL_MS = 3000;

// No NEW frame for this long (while visible + playing) means the feed is frozen. The key
// Android freeze (crbug.com/1401352) leaves the MediaStreamTrack reporting live/unmuted
// while no frames flow, so freeze must be judged by frame PROGRESS, not track state. Keep
// this >= ~1.5s so a low/variable webcam fps never trips it.
const FRAME_STALL_THRESHOLD_MS = 2000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// requestVideoFrameCallback / getVideoPlaybackQuality aren't in every TS lib version; treat
// them as optional so detection degrades gracefully where they're missing.
type FrameTimingVideo = HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: () => void) => number;
    cancelVideoFrameCallback?: (handle: number) => void;
    getVideoPlaybackQuality?: () => { totalVideoFrames: number };
    webkitDecodedFrameCount?: number;
};

const formatMediaError = (err: unknown, context: string): string => {
    if (!(err instanceof Error)) return `An unknown error occurred while ${context}.`;
    const messageMap: Record<string, string> = {
        NotAllowedError: 'Webcam access denied. Please allow camera permissions in your browser settings.',
        PermissionDeniedError: 'Webcam access denied. Please allow camera permissions in your browser settings.',
        NotFoundError: 'No webcam found or permission issue. Please connect a webcam and grant permissions.',
        DevicesNotFoundError: 'No webcam found or permission issue. Please connect a webcam and grant permissions.',
    };
    return messageMap[err.name] ?? `Error ${context}: ${err.message}`;
};

function stopMediaStream(stream: MediaStream | null | undefined) {
    stream?.getTracks().forEach((track) => track.stop());
}

export class MediaDeviceStore extends DisposableBase {
    @observable accessor videoElementRef = React.createRef<HTMLVideoElement | null>();
    @observable accessor loading = false;
    @observable accessor devices: MediaDeviceInfo[] = [];
    @observable accessor error: string | null = null;
    @observable accessor permissionGranted: boolean | null = null;
    @observable accessor selectedDeviceId: string | null = null;
    @observable accessor stream: MediaStream | null = null;

    private settingsStore: SettingsStore | null = null;
    private _streamRequestId = 0;
    private _devicesInFlight: Promise<void> | null = null;
    /** Guard Mode auto-select: open an environment-facing camera when none was pinned. */
    private _useEnvironmentFacing = false;
    /** Re-entrancy guard so overlapping resume events don't open the camera repeatedly. */
    private _reacquiring = false;
    private _watchdog: ReturnType<typeof setInterval> | null = null;
    // Frame-progress monitor (the only reliable freeze signal for the zombie-track case).
    private _lastFrameTs = 0;
    private _rvfcHandle = 0;
    private _rvfcVideo: HTMLVideoElement | null = null;
    private _lastFrameCount = -1;
    private _lastFrameCountTs = 0;
    // Frame-stall must persist across consecutive watchdog ticks before re-acquiring, so a
    // single slow inter-frame gap (low-fps webcam, cold compositor on resume) can't trip it.
    private _stallStrikes = 0;
    // The in-flight getStream() launched by setSelectedDeviceId, so re-acquire can await it
    // instead of starting a second, racing open.
    private _lastOpen: Promise<void> | null = null;

    async initialize(settingsStore?: SettingsStore) {
        this.settingsStore = settingsStore ?? null;
        this._installResumeRecovery();
        await this.getMediaDevices();
    }

    /**
     * Recover the camera when the app returns to the foreground. Android ends or mutes the
     * stream's tracks while backgrounded and nothing else re-acquires them, leaving the
     * <video> frozen on its last frame until a manual refresh. WebViews are inconsistent
     * about which lifecycle event fires on resume (power button, app switch, lock screen),
     * so we listen to several; ensureStreamLive() is a no-op when the stream is already
     * live, so redundant triggers are harmless.
     */
    private _installResumeRecovery() {
        if (typeof document === 'undefined' || typeof window === 'undefined') return;
        // Re-acquire UNCONDITIONALLY on resume. On this Android WebView the freeze
        // (crbug.com/1401352) leaves the track reporting live/unmuted with no mute/ended,
        // so detection at the resume instant is unreliable — the only dependable fix is to
        // tear down and rebuild the capture pipeline whenever the app comes back. The
        // retry/backoff in _reopenWithRetry covers the camera-still-releasing window.
        const onResume = (reason: string) => {
            this._stallStrikes = 0;
            void this.ensureStreamLive(reason, true);
        };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') onResume('visibilitychange');
        };
        const onFocus = () => onResume('focus');
        const onPageShow = () => onResume('pageshow');
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onFocus);
        window.addEventListener('pageshow', onPageShow);

        // Safety net for WebViews that fire none of the above on resume. The interval is
        // paused while the app is backgrounded and resumes ticking once it's active again;
        // it only acts (and only logs) when the stream is actually frozen, so it's quiet
        // during normal operation.
        this._watchdog = setInterval(this._watchdogTick, STREAM_WATCHDOG_INTERVAL_MS);

        this._disposers.push(() => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('pageshow', onPageShow);
            if (this._watchdog) clearInterval(this._watchdog);
            this._watchdog = null;
            this._stopFrameMonitor();
        });
    }

    private _watchdogTick = () => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        if (this._reacquiring) return;
        this._ensureFrameMonitorAttached();

        // A dead track (ended/muted) is unambiguous — recover immediately.
        if (this._isFrozen()) {
            this._stallStrikes = 0;
            void this.ensureStreamLive('watchdog');
            return;
        }
        // A frame-stall is judged by timing, which can have transient false positives (a
        // low-fps webcam gap, the compositor warming up after resume). Require it to persist
        // across consecutive ticks before tearing the camera down.
        if (this._framesStalled() || this._framesStalledBackup()) {
            this._stallStrikes++;
            if (this._stallStrikes >= 2) {
                this._stallStrikes = 0;
                void this.ensureStreamLive('watchdog-stall');
            }
        } else {
            this._stallStrikes = 0;
        }
    };

    @action setVideoElementRef = (newRef: React.RefObject<HTMLVideoElement | null>) => {
        this.videoElementRef = newRef;
    };

    @action createNewVideoElementRef = () => {
        this.videoElementRef = React.createRef<HTMLVideoElement | null>();
        return this.videoElementRef;
    };

    @action setSelectedDeviceId = (newDeviceId: string | null) => {
        if (this.selectedDeviceId === newDeviceId) return;
        this.selectedDeviceId = newDeviceId;
        // Keep the open's promise so a re-acquire that triggered this selection can await it
        // rather than starting a second, racing getUserMedia.
        this._lastOpen = this.getStream();
    };

    stopStream() {
        this._streamRequestId++;
        if (!this.stream) return;
        stopMediaStream(this.stream);
        this.stream = null;
    }

    /** Stream is missing, ended, or muted — i.e. the <video> can't be showing live frames. */
    private _isFrozen(): boolean {
        const tracks = this.stream?.getVideoTracks() ?? [];
        return tracks.length === 0 || tracks.some((t) => t.readyState !== 'live' || t.muted);
    }

    /** Stream has a usable (non-ended) video track — the success condition after a re-open. */
    private _hasLiveTrack(): boolean {
        const tracks = this.stream?.getVideoTracks() ?? [];
        return tracks.length > 0 && tracks.every((t) => t.readyState === 'live');
    }

    /** Recovery is needed if the track is dead OR frames have stopped flowing (zombie track). */
    private _needsRecovery(): boolean {
        return this._isFrozen() || this._framesStalled() || this._framesStalledBackup();
    }

    /**
     * Primary freeze signal: requestVideoFrameCallback fires only when a genuinely new frame
     * is presented, so on the Android zombie-track freeze (track still 'live', no frames) it
     * stops firing and _lastFrameTs goes stale. True once that exceeds the threshold while the
     * element is visible and playing.
     */
    private _framesStalled(): boolean {
        const video = this.videoElementRef.current;
        // Only judge when rVFC is monitoring THIS element (not detached, not a stale/swapped one).
        if (!video || !video.isConnected || this._rvfcVideo !== video) return false;
        if (video.paused || video.readyState < 2) return false; // paused / no current frame yet
        if (this._lastFrameTs === 0) return false;           // not seeded yet
        return performance.now() - this._lastFrameTs > FRAME_STALL_THRESHOLD_MS;
    }

    /**
     * Backup freeze signal for WebViews without requestVideoFrameCallback: the decoded-frame
     * counter not advancing across the threshold. getVideoPlaybackQuality is spec-ambiguous
     * for live MediaStreams, so this is only used when rVFC is unavailable.
     */
    private _framesStalledBackup(): boolean {
        const video = this.videoElementRef.current as FrameTimingVideo | null;
        if (!video || !video.isConnected || this._rvfcVideo) return false; // detached / rVFC carries it
        if (video.paused || video.readyState < 2) return false;
        const count = typeof video.getVideoPlaybackQuality === 'function'
            ? video.getVideoPlaybackQuality().totalVideoFrames
            : video.webkitDecodedFrameCount;
        if (typeof count !== 'number') return false;
        const now = performance.now();
        if (count !== this._lastFrameCount) {
            this._lastFrameCount = count;
            this._lastFrameCountTs = now;
            return false;
        }
        return this._lastFrameCountTs !== 0 && now - this._lastFrameCountTs > FRAME_STALL_THRESHOLD_MS;
    }

    /** Milliseconds since the last presented frame (rVFC path only); null if not monitoring. */
    private _frameAgeMs(): number | null {
        if (!this._rvfcVideo || this._lastFrameTs === 0) return null;
        return Math.round(performance.now() - this._lastFrameTs);
    }

    /** Re-arm the frame monitor if the bound <video> element changed or a stream just opened. */
    private _ensureFrameMonitorAttached() {
        const video = this.videoElementRef.current;
        // A detached element (e.g. navigated to Setup, unmounting VideoDisplay) stops getting
        // rVFC callbacks but keeps paused=false/readyState=4 — tear the monitor down so it
        // isn't mistaken for a freeze.
        if (!video || !video.isConnected || !this.stream) { this._stopFrameMonitor(); return; }
        if (this._rvfcVideo !== video) this._startFrameMonitor();
    }

    /** Reset the frame-progress timers so the next window starts clean (e.g. on resume / re-open). */
    private _seedFrameMonitor() {
        this._lastFrameTs = performance.now();
        this._lastFrameCount = -1;
        this._lastFrameCountTs = 0;
    }

    private _startFrameMonitor() {
        const video = this.videoElementRef.current as FrameTimingVideo | null;
        this._stopFrameMonitor();
        if (!video || typeof video.requestVideoFrameCallback !== 'function') {
            this._rvfcVideo = null;   // rVFC unavailable → _framesStalledBackup carries detection
            return;
        }
        this._rvfcVideo = video;
        this._lastFrameTs = performance.now();   // seed: don't report frozen before the first frame
        const onFrame = () => {
            this._lastFrameTs = performance.now();
            this._rvfcHandle = video.requestVideoFrameCallback!(onFrame);
        };
        this._rvfcHandle = video.requestVideoFrameCallback(onFrame);
    }

    private _stopFrameMonitor() {
        const video = this._rvfcVideo as FrameTimingVideo | null;
        if (video && this._rvfcHandle && typeof video.cancelVideoFrameCallback === 'function') {
            video.cancelVideoFrameCallback(this._rvfcHandle);
        }
        this._rvfcHandle = 0;
        this._rvfcVideo = null;
    }

    /** Log track mute/unmute/ended transitions to diagnose background/resume freezes. */
    private _attachTrackDiagnostics(stream: MediaStream) {
        const track = stream.getVideoTracks()[0];
        if (!track) return;
        const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible';
        const page = () => (typeof document !== 'undefined' ? document.visibilityState : 'n/a');
        track.onmute = () => debugLog.warn(`Camera track MUTED (page ${page()})`);
        track.onunmute = () => debugLog.info(`Camera track unmuted (page ${page()})`);
        track.onended = () => {
            debugLog.warn(`Camera track ENDED (page ${page()})`);
            // Recover now if we're in the foreground; otherwise the watchdog/resume events
            // handle it when the app comes back (re-acquiring while hidden just fails).
            if (visible()) void this.ensureStreamLive('track-ended');
        };
    }

    /** Human-readable track state for the on-screen debug log. */
    private _describeStream(): string {
        const tracks = this.stream?.getVideoTracks() ?? [];
        if (tracks.length === 0) return 'no stream';
        return tracks
            .map((t) => `${t.label || t.kind}[readyState=${t.readyState} muted=${t.muted} enabled=${t.enabled}]`)
            .join(', ');
    }

    /**
     * Re-acquire the camera if the active stream is no longer showing live frames. Android
     * ends OR mutes the stream's tracks while backgrounded, which leaves the <video> frozen
     * on its last frame with no way to recover short of a manual refresh — there is no
     * remount to re-run getMediaDevices(). Wired to resume events so the camera reopens
     * automatically. `force` skips the freeze check (used on resume, where the track can't
     * be trusted); otherwise it's a no-op when the stream still looks live, so it's safe to
     * call often. `reason` is logged so on-device diagnostics show what triggered it.
     */
    async ensureStreamLive(reason = 'manual', force = false) {
        const age = this._frameAgeMs();
        debugLog.info(`Camera check (${reason}): ${this._describeStream()}${age !== null ? ` | lastFrame ${age}ms ago` : ''}`);
        if (!force && !this._needsRecovery()) return;
        if (this._reacquiring) return; // a re-acquire from an earlier resume event is in flight
        this._reacquiring = true;
        try {
            debugLog.warn(`Camera re-acquire (${reason}${force ? ', forced' : ''})`);
            // Drop the dead stream so VideoDisplay re-binds (and restarts detection) when a
            // fresh one arrives.
            this.stopStream();
            await this._reopenWithRetry();
        } finally {
            this._reacquiring = false;
            // Re-arm the monitor on the (possibly new) element and give the fresh stream a
            // full window to deliver its first frame before the watchdog judges it again.
            this._seedFrameMonitor();
            this._startFrameMonitor();
        }
    }

    /**
     * Re-open the camera, retrying with a backoff. On resume the OS often hasn't released
     * the camera yet, so the first getUserMedia throws NotReadableError; later attempts
     * succeed. Re-opens the current device first (same camera); after a couple of failures
     * it clears the selection so getMediaDevices() re-enumerates and re-selects (handles a
     * deviceId that changed across the teardown).
     */
    private async _reopenWithRetry() {
        for (let attempt = 0; attempt < REACQUIRE_RETRY_DELAYS_MS.length; attempt++) {
            if (REACQUIRE_RETRY_DELAYS_MS[attempt] > 0) await sleep(REACQUIRE_RETRY_DELAYS_MS[attempt]);

            if (this.selectedDeviceId) {
                await this.getStream();             // fast path: re-open the known device
            } else {
                await this.getMediaDevices();       // re-enumerate + re-select
                // getMediaDevices opens the stream un-awaited via setSelectedDeviceId; await
                // that same open rather than starting a second one that races it.
                const open = this._lastOpen;
                if (open) await open;
            }

            if (this._hasLiveTrack()) {
                debugLog.info(`Camera re-acquired (attempt ${attempt + 1}): ${this._describeStream()}`);
                return;
            }
            debugLog.warn(`Re-open attempt ${attempt + 1} failed: ${this.error ?? 'no live track'}`);
            if (attempt === 1) this.selectedDeviceId = null; // fall back to re-enumeration
        }
        debugLog.error('Camera re-acquire gave up after all retries');
    }

    async getStream() {
        const requestId = ++this._streamRequestId;
        this.error = null;
        this.loading = true;

        const video = this._videoConstraints();
        if (!video) {
            this.loading = false;
            return;
        }

        try {
            const newStream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
            if (requestId !== this._streamRequestId) {
                stopMediaStream(newStream);
                return;
            }
            // When opened by facingMode (no explicit deviceId), record which device the
            // browser actually chose so selection state and the settings dropdown stay
            // in sync and any later re-selection is stable.
            const openedId = newStream.getVideoTracks()[0]?.getSettings().deviceId;
            if (openedId && openedId !== this.selectedDeviceId) {
                this.selectedDeviceId = openedId;
            }
            const previousStream = this.stream;
            this.stream = newStream;
            if (previousStream !== newStream) {
                stopMediaStream(previousStream);
            }
            this._attachTrackDiagnostics(newStream);
            debugLog.info(`Camera opened: ${this._describeStream()}`);
        } catch (err) {
            if (requestId !== this._streamRequestId) return;
            console.error(`Error starting stream (${this.selectedDeviceId ?? 'environment-facing'}):`, err);
            this.error = formatMediaError(err, 'starting webcam');
            debugLog.error(`Camera open failed: ${this.error}`);
        } finally {
            if (requestId === this._streamRequestId) {
                this.loading = false;
            }
        }
    }

    /**
     * Video constraints for the active selection. An explicit device is opened by exact
     * id; otherwise, when a rear camera is wanted but couldn't be identified by id/label
     * (labels are frequently blank on a cold start), we ask the OS for an
     * environment-facing camera rather than guessing a device index.
     */
    private _videoConstraints(): MediaTrackConstraints | null {
        if (this.selectedDeviceId) {
            return { deviceId: { exact: this.selectedDeviceId }, ...VIDEO_CONSTRAINTS };
        }
        if (this._useEnvironmentFacing) {
            return { facingMode: { ideal: 'environment' }, ...VIDEO_CONSTRAINTS };
        }
        return null;
    }

    /**
     * Enumerates cameras and auto-selects one. Coalesced so the several mount-time
     * callers (store init, CamArea, GuardScreen) can't race and each pick a different
     * camera on a cold start.
     */
    getMediaDevices(): Promise<void> {
        if (this._devicesInFlight) return this._devicesInFlight;
        this._devicesInFlight = this._enumerateAndSelect().finally(() => {
            this._devicesInFlight = null;
        });
        return this._devicesInFlight;
    }

    private async _enumerateAndSelect() {
        this.error = null;
        this.loading = true;

        try {
            const tempStream = await navigator.mediaDevices.getUserMedia({ video: VIDEO_CONSTRAINTS, audio: false });
            this.permissionGranted = true;
            tempStream.getTracks().forEach((track) => track.stop());

            const allDevices = await navigator.mediaDevices.enumerateDevices();
            this.devices = allDevices.filter((d) => d.kind === 'videoinput');

            if (this.devices.length === 0) {
                this.error = 'No webcams found. Please ensure a webcam is connected.';
                return;
            }

            const needsSelection = !this.selectedDeviceId || !this.devices.some((d) => d.deviceId === this.selectedDeviceId);
            if (needsSelection) await this._autoSelectCamera();
        } catch (err) {
            this.permissionGranted = false;
            this.error = formatMediaError(err, 'accessing webcam');
        } finally {
            this.loading = false;
        }
    }

    /**
     * Picks a camera when none is currently usable. Priority: the user's preferred label,
     * then (Guard Mode) a rear camera by label, then an OS-resolved environment-facing
     * camera (Guard Mode) or devices[0] (Kiosk Mode). The environment-facing step is what
     * stops Guard Mode landing on the FRONT camera — whether because labels are blank on a
     * cold start or the preferred device is absent — instead of guessing devices[0], which
     * is often the front lens.
     */
    private async _autoSelectCamera() {
        const isGuard = this.settingsStore?.appMode === 'guard';
        const preferredLabel = this.settingsStore?.preferredCameraLabel ?? null;

        let preferred = preferredLabel
            ? this.devices.find((d) => d.label === preferredLabel)
            : undefined;
        if (!preferred && isGuard) {
            preferred = this.devices.find((d) => /back|rear|environment/i.test(d.label));
        }

        if (preferred) {
            this._useEnvironmentFacing = false;
            this.setSelectedDeviceId(preferred.deviceId);
            return;
        }

        if (isGuard) {
            // Guard Mode couldn't resolve a real rear camera — either no preference was
            // set, or the preferred device is absent (config URL camera not connected, a
            // stale localStorage label, blank labels on a cold start). Let the OS resolve
            // an environment-facing camera rather than dropping to devices[0] (the front
            // lens); getStream adopts whichever deviceId it actually opens.
            this._useEnvironmentFacing = true;
            this.selectedDeviceId = null;
            await this.getStream();
            return;
        }

        // Kiosk Mode has no rear preference: default to the first enumerated camera.
        this._useEnvironmentFacing = false;
        this.setSelectedDeviceId(this.devices[0].deviceId);
    }

    dispose() {
        this._stopFrameMonitor();
        this.stopStream();
        super.dispose();
    }
}
